import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import Download from "./Download";
import Projects from "./Projects";
import TrackingStatus from "./TrackingStatus";
import { fetchAccounts, fetchProjectsWithSubSupliers, fetchProjectsWithTimeEntries } from "./services/HarvestService";
import { getDate, parse, add} from "date-fns";
import { pdf } from "@react-pdf/renderer";
import PdfInvoice from "./PdfInvoice";
import { getMonthNameByPeriod, getPeriodAddMonth, getPeriodNameByPeriod } from "./utiil/Dates";
import { currencyFormate } from "./utiil/Formate";
import Summary from "./Summary";


const Accounts = () => {
  const now = new Date();
  const defaultDate = now.getDate() >= 20 ? now : add(now, {months: -1});
  const [accounts, setAccounts] = useState([]);
  const [missingByCompany, setMissingByCompany] = useState({});
  const [trackingReady, setTrackingReady] = useState(false);
  const [waitingForData, setWaitingForData] = useState(true);
  const [downloadStarted, setDownloadStarted] = useState(false)
  const [period, setPeriod] = useState({
    year: defaultDate.getFullYear(),
    month: defaultDate.getMonth(),
  });
  const periodName = getPeriodNameByPeriod(period);

  
  useEffect(() => {
    
    const updateWithProjectsWithTimeEntries = async (allAccounts, period, account, projectWithSubSupliers) => {
      const accountId = account.id;
      const data = await fetchProjectsWithTimeEntries(accountId, period);
      
      for (const entry of data.time_entries) {  
        const hours = entry.rounded_hours ? parseFloat(entry.rounded_hours) : parseFloat(entry.hours);
        const spent_date = getDate(parse(entry.spent_date, "yyyy-MM-dd", new Date()));
        const amount = hours * entry.billable_rate;
        addEntry(
          allAccounts,
          period,
          account,
          entry.client,
          entry.project,
          entry.user,
          entry.task.name,
          spent_date,
          hours,
          amount, false
        );
        
        if(projectWithSubSupliers[entry.project.code + "-S"]){
          const subSupProject = projectWithSubSupliers[entry.project.code + "-S"]
          addEntry(
            allAccounts,
            period,
            subSupProject.account,
            subSupProject.client,
            subSupProject,
            entry.user,
            entry.task.name,
            spent_date,
            hours,
            amount,
            true
          );
        }
      };
    };

    const setHarvestData = async () => {
      setWaitingForData(true);
      setTrackingReady(false);

      const projectWithSubSupliers = {}
      const harvestAccounts = await fetchAccounts();
      
      for (const account of harvestAccounts) {
        const projects = await fetchProjectsWithSubSupliers(account.id);
        for (const project of projects) {
          projectWithSubSupliers[project.code] = {account:  {id:account.id, name:account.name} , ...project}
        }
      }
      const allAccounts = {};
      for (const account of harvestAccounts) {
        await updateWithProjectsWithTimeEntries(allAccounts, period, account, projectWithSubSupliers);
      }
      setAccounts(allAccounts);
      setWaitingForData(false);
    };

    setHarvestData();
  }, [period]);

  const handleDownloadClick = async () => {

    setDownloadStarted(true);
    const zip = new JSZip();
    for (const account of Object.values(accounts)) {
      for (const project of Object.values(account.projects)) {
        if (project.client.name !== "Varianttid") {
          const illegalCharsRegEx = /[|&;$%@"<>/+,]/g;
          
          const fileName = account.name +
          "/" +
          (project.client.name +
          "-" +
          project.code +
          "-" +
          project.name +
          ".pdf").replace(illegalCharsRegEx, "_");

          zip.file(
            fileName,
            pdf(<PdfInvoice project={project} />).toBlob()
          );
        }
      }
    }

    const blob = await zip.generateAsync({ type: "blob" })
    saveAs(blob,  (period.month+1).toString().padStart(2, '0') + " - " + getMonthNameByPeriod(period) + " - Fakturagrunnlag.zip");
    setDownloadStarted(false);
  };

  const handleStatusReady = useCallback((data) => {
    setMissingByCompany(data);
    setTrackingReady(true);
  }, []);

  const onAddMounth = (step) => {
    setWaitingForData(true);
    setTrackingReady(false);
    setPeriod(getPeriodAddMonth(period, step));
  };

  const accountValues = Object.values(accounts);

  const accountList = accountValues.map((account) => {
    return (
      <div className="accounts" key={account.id} id={`account-${account.id}`}>
        <h3>
          {account.name} ({currencyFormate(account.sum_amount)} kr)
        </h3>
        <Projects projects={account.projects} />
      </div>
    );
  });

  return (
    <>
      <header className="App-header">
        <svg className="variant-logo" viewBox="0 0 91 90" width="24" height="24" aria-hidden="true">
          <path d="M21.6 23L38.4896 68.6147H38.4921C39.5027 71.3457 42.118 73.1614 45.0414 73.1614C47.9598 73.1614 50.5702 71.3555 51.5857 68.6319L68.611 23H58.5467L45.0685 61.0278L31.5213 23H21.6Z" fill="currentColor"/>
          <path fillRule="evenodd" d="M90.2 44.8C90.2 69.5424 70.1423 89.6 45.4 89.6C20.6576 89.6 0.599976 69.5424 0.599976 44.8C0.599976 20.0576 20.6576 0 45.4 0C70.1423 0 90.2 20.0576 90.2 44.8ZM87.07 44.8C87.07 67.8137 68.4137 86.47 45.4 86.47C22.3863 86.47 3.72998 67.8137 3.72998 44.8C3.72998 21.7863 22.3863 3.13 45.4 3.13C68.4137 3.13 87.07 21.7863 87.07 44.8Z" fill="currentColor"/>
        </svg>
        <h1>Invoice Details</h1>
        <div className="period-select">
          <button onClick={() => onAddMounth(-1)}>←</button>
          <span className="period-name">{periodName}</span>
          <button onClick={() => onAddMounth(1)}>→</button>
        </div>
        <Download downloading={downloadStarted} handleOnClick={handleDownloadClick} />
      </header>
      {!waitingForData && (
        <nav className="company-nav">
          {accountValues.map(account => (
            <a key={account.id} href={`#account-${account.id}`} className="company-nav-link">
              {account.name}
            </a>
          ))}
          <TrackingStatus accounts={accounts} period={period} waitingForData={waitingForData} onStatusReady={handleStatusReady} />
        </nav>
      )}
      {!waitingForData && (
        <Summary accounts={accounts} missingByCompany={missingByCompany} trackingReady={trackingReady} />
      )}
      {waitingForData && (
        <div className="waiting-for-data">
          <h3>Waiting for data...</h3>
        </div>
      )}
      {!waitingForData && (
        <div className="account-list">{accountList}</div>
      )}
    </>
  );
};
export default Accounts;

const addEntry = (accounts, period, account, client, project, user, task, spent_date, hours, amount, isSubSuplier) => {
  
  if (!accounts[account.id]) {
    accounts[account.id] = account;
    accounts[account.id].projects = {} 
    accounts[account.id].sum_amount = 0;
    accounts[account.id].sum_hours = 0;
  }
  const projects = accounts[account.id].projects;
  
  const projectId = project.code || project.id;
  if (!projects[projectId]){
    projects[projectId] = {
      period: period,
      entries: {},
      client: client,
      ...project,
      sum_amount: 0,
      sum_hours: 0,
      users: {},
      data:[]
    }
  }
  projects[projectId].data.push({spent_date, period, user, task,   hours, amount, isSubSuplier})

  if (!(projects[projectId].entries[spent_date])) {
    projects[projectId].entries[spent_date] = 0;
  }
  
  const userId = user.id;
  const users = projects[projectId].users;
  if (!(users[userId])) {
    users[userId] = {
      ...user,
      sum_hours: 0,
      entries: {},
      isSubSuplier: isSubSuplier
    };
  }
  
  const entries = users[userId].entries;
  if (!entries[spent_date]) {
    entries[spent_date] = {
      hours: 0,
      amount: 0,
    };
  }

  projects[projectId].entries[spent_date] += hours;
  projects[projectId].sum_hours += hours;
  projects[projectId].sum_amount += amount;
  users[userId].sum_hours += hours;
  users[userId].amount += amount;
  entries[spent_date].amount += amount;
  entries[spent_date].hours += hours;
  if (!isSubSuplier){
    accounts[account.id].sum_hours += hours;
    accounts[account.id].sum_amount += amount;
  } 

  return accounts;
};
